import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  RadioGroup,
  Radio,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  FormHelperText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { de } from 'date-fns/locale';
import { deDE } from '@mui/x-date-pickers/locales';
import InfoboxSchutz from '../components/InfoboxSchutz';
import './PersoenlicheAngaben.css';
import { useAppContext } from '../context/AppContext';

import { applicationService } from '../backend/applicationService';
import defaultApplication from '../backend/default.application.json';
import * as yup from 'yup';
import { isValid, startOfDay, subYears, isBefore, isEqual } from 'date-fns';
import { useYupValidation } from '../hooks/useYupValidation';
import countryCodes from '../static/countryCodes.json';
import GlobalErrorAlert from '../components/GlobalErrorAlert';
import { useGlobalError } from '../context/GlobalErrorContext';
import { anredeToIsoNamingForSexMap } from '../util/helper';

const validationRules = yup.object().shape({
  geschlecht: yup.string().required('Bitte auswählen').oneOf(['frau', 'herr', 'divers'], 'Bitte auswählen'),
  vorname: yup.string().required('Bitte Vornamen eingeben'),
  nachname: yup.string().required('Bitte Nachnamen eingeben'),
  geburtsdatum: yup
    .date()
    .typeError('Das eingegebene Datum muss dem Format TT.MM.JJJJ entsprechen')
    .required('Das eingegebene Datum muss dem Format TT.MM.JJJJ entsprechen')
    .test('min-18-years', 'Das ausgewählte Datum muss mindestens 18 Jahre zurück liegen', value => {
      if (!value) {
        return false;
      }
      const eighteenYearsAgo = subYears(new Date(), 18);
      const eighteenYearsAgoAtStartOfDay = startOfDay(eighteenYearsAgo);
      const valueAtStartOfDay = startOfDay(value);

      return (
        isBefore(valueAtStartOfDay, eighteenYearsAgoAtStartOfDay) ||
        isEqual(valueAtStartOfDay, eighteenYearsAgoAtStartOfDay)
      );
    }),
  plz: yup
    .string()
    .required('Bitte Postleitzahl eingeben')
    .matches(/^\d{5}$/, 'Postleitzahl muss aus fünf Ziffern bestehen'),
  ort: yup.string().required('Bitte Ort eingeben'),
  strasse: yup.string().required('Bitte Straße eingeben'),
  hausnummer: yup.string().required('Bitte Hausnummer eingeben'),
  staatsangehoerigkeit: yup
    .string()
    .required('Bitte Staatsangehörigkeit angeben')
    .oneOf(
      countryCodes.map(cC => cC.code),
      'Ungültige Staatsangehörigkeit',
    ),
  email: yup.string().required('Bitte E-Mail-Adresse eingeben').email('Bitte eine gültige E-Mail-Adresse eingeben'),
  telefonnummer: yup
    .string()
    .notRequired()
    .matches(
      /^\+?[0-9 -]*$/,
      'Bitte eine gültige Telefonnummer eingeben oder leer lassen, ' +
        'z.B. +49123456789, +49 123 456789 oder 0123 456789',
    ),
});

const PersoenlicheAngaben = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { state, dispatch } = useAppContext();
  const { showGlobalError, clearGlobalError } = useGlobalError();

  const [formData, setFormData] = useState({
    geschlecht: '',
    vorname: '',
    nachname: '',
    geburtsdatum: null,
    plz: '',
    ort: '',
    strasse: '',
    hausnummer: '',
    staatsangehoerigkeit: null,
    email: '',
    telefonnummer: '',
    ...state.formData,
  });

  const { errors, fieldRefs, validateField, validateForm } = useYupValidation(validationRules);

  const handleInputChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBlur = async event => {
    const { name, value } = event.target;
    await validateField(name, value);
  };

  const handleDateChange = date => {
    setFormData(prev => ({ ...prev, geburtsdatum: date }));
    if (isValid(date)) {
      validateField('geburtsdatum', date);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    clearGlobalError();

    if (await validateForm(formData)) {
      const application = defaultApplication;
      const versicherungsBeginn = new Date(formData.versicherungsbeginn);
      versicherungsBeginn.setHours(versicherungsBeginn.getHours() + 3); //Entgehen wir dem GMT+1/+2 Problem
      application.modalities.beginDate = versicherungsBeginn.toISOString().split('T')[0];
      application.modalities.period.code = formData.zahlungsintervall;

      application.applicant.sex = anredeToIsoNamingForSexMap[formData.geschlecht];
      application.applicant.firstname = formData.vorname;
      application.applicant.surname = formData.nachname;
      application.applicant.birthdate = formData.geburtsdatum.toISOString().split('T')[0];
      application.applicant.nationalityCode = formData.staatsangehoerigkeit;
      application.applicant.address.zip = formData.plz;
      application.applicant.address.city = formData.ort;
      application.applicant.address.street = formData.strasse;
      application.applicant.address.streetNo = formData.hausnummer;

      const contacts = [];
      contacts.push({
        type: 'email',
        label: 'E-Mail',
        data: formData.email,
      });
      if (formData.telefonnummer) {
        contacts.push({
          type: 'phone',
          label: 'Telefonnummer',
          data: formData.telefonnummer,
        });
      }

      application.applicant.contact = contacts;
      application.status = 'TEMPORARY';

      const selectedProduct = state.selectedProduct;
      selectedProduct.options = selectedProduct.options.filter(o => state.formData.selectedOptions.includes(o.code));
      application.chosenProductsAndOptions = [selectedProduct];

      dispatch({ type: 'SET_FORM_DATA', payload: formData });
      if (state.editMode) {
        dispatch({
          type: 'SET_APPLICATION_DATA',
          payload: {
            applicationObject: application,
            applicationId: state.applicationId,
            email: state.formData.email,
          },
        });
        dispatch({ type: 'SET_CURRENT_STEP', payload: 4 });
        navigate('/deine-zahlungsdaten');
      } else {
        const applicationId = applicationService.generateApplicationId();
        dispatch({ type: 'SET_APPLICATION_ID', payload: applicationId });

        const response = await applicationService.requestOptIn(applicationId, application);

        if (response.success) {
          dispatch({ type: 'SET_CURRENT_STEP', payload: 3 });
          navigate('/e-mail-bestaetigung');
        } else {
          showGlobalError('Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
        }
      }
    } else {
      // Scroll zum ersten Fehler
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementsByName(firstErrorField)[0];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const handleBack = () => {
    clearGlobalError();
    dispatch({ type: 'SET_FORM_DATA', payload: formData });
    navigate('/tarif-berechnen');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
      <Box className="wrapper" role="main" aria-label="Eingabe der persönlichen Daten">
        <Container className="main-content-scroll-container">
          <Box className="main-content-with-sidebar">
            {/* --- PAGE CONTENT  --- */}
            <Box className="page-content">
              <Typography variant="h1" component="h1" className="headline-large-bold">
                Persönliche Angaben
              </Typography>

              <p style={{ marginTop: '16px' }}> Mit * gekennzeichnete Felder sind Pflichtfelder. </p>

              <GlobalErrorAlert sx={{ mt: 1 }} />

              <Box component="form" name="persoenlicheAngaben" role="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={3} className="form-wrapper">
                  {/* Hauptformular */}
                  <Grid item xs={12} lg={8} className="form-inner-grid">
                    <Grid container spacing={3} className="form-content-container">
                      <Box>
                        {/* Geschlecht */}
                        <Grid item xs={12}>
                          <FormControl component="fieldset" error={!!errors.geschlecht}>
                            <FormLabel
                              component="legend"
                              sx={{
                                position: 'absolute',
                                left: '-10000px',
                                width: '1px',
                                height: '1px',
                                overflow: 'hidden',
                              }}
                            >
                              Geschlecht
                            </FormLabel>
                            <RadioGroup
                              row
                              name="geschlecht"
                              value={formData.geschlecht}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              data-testid="anrede-radiogroup"
                            >
                              <FormControlLabel
                                value="herr"
                                control={
                                  <Radio
                                    slotProps={{ input: { ref: fieldRefs.geschlecht } }}
                                    id="jumpmark-maincontent"
                                    tabIndex={-1}
                                  />
                                }
                                label="Herr"
                                className="span-bold-700"
                                sx={{ fontWeight: '700 !important' }}
                              />
                              <FormControlLabel
                                value="frau"
                                control={<Radio />}
                                label="Frau"
                                className="span-bold-700"
                                sx={{ fontWeight: '700 !important' }}
                              />
                              <FormControlLabel
                                value="divers"
                                control={<Radio />}
                                label="Divers"
                                className="span-bold-700"
                                sx={{ fontWeight: '700 ! important' }}
                              />
                            </RadioGroup>
                            <FormHelperText data-testid="anrede-error">{errors.geschlecht}</FormHelperText>
                          </FormControl>
                        </Grid>
                      </Box>
                      <Box>
                        {/* Vorname */}
                        <Grid item xs={12} md={6}>
                          <TextField
                            inputRef={fieldRefs.vorname}
                            variant="filled"
                            className="input-field larger-input-text"
                            fullWidth
                            label="Vorname"
                            name="vorname"
                            value={formData.vorname}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            error={!!errors.vorname}
                            helperText={errors.vorname}
                            autoComplete="given-name"
                            slotProps={{
                              formHelperText: { 'data-testid': 'vorname-error' },
                            }}
                          />
                        </Grid>
                      </Box>

                      {/* Nachname */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          inputRef={fieldRefs.nachname}
                          variant="filled"
                          className="input-field larger-input-text"
                          fullWidth
                          label="Nachname"
                          name="nachname"
                          value={formData.nachname}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          required
                          error={!!errors.nachname}
                          helperText={errors.nachname}
                          autoComplete="family-name"
                          slotProps={{
                            formHelperText: { 'data-testid': 'nachname-error' },
                          }}
                        />
                      </Grid>
                      <Box className="form-input-flex">
                        {/* Geburtsdatum */}
                        <Grid item xs={12} md={6} className="form-item-flex-1">
                          <LocalizationProvider
                            dateAdapter={AdapterDateFns}
                            adapterLocale={de}
                            localeText={deDE.components.MuiLocalizationProvider.defaultProps.localeText}
                          >
                            <DatePicker
                              label="Geburtsdatum *"
                              value={formData.geburtsdatum}
                              onChange={handleDateChange}
                              required
                              format="dd.MM.yyyy"
                              disableFuture={true}
                              sx={{
                                '& .MuiIconButton-root:focus-visible': {
                                  boxShadow: '0 0 0 2px var(--color-secondary)',
                                  borderRadius: '2px', // Definiert die Eckigkeit
                                },
                                // Border immer mit --color-secondary
                                '& .MuiPickersOutlinedInput-notchedOutline': {
                                  borderColor: 'var(--color-secondary) !important',
                                },
                                // Input-Feld Focus Outline
                                '& .MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
                                  border: '2px solid var(--color-secondary) !important',
                                },
                              }}
                              slotProps={{
                                textField: {
                                  id: 'geburtsdatum',
                                  inputRef: fieldRefs.geburtsdatum,
                                  fullWidth: true,
                                  variant: 'outlined',
                                  className: 'date-input',
                                  error: !!errors.geburtsdatum,
                                  helperText: errors.geburtsdatum,
                                  onBlur: event => {
                                    // check, that complete field is left, not only one section
                                    const root = event.currentTarget;
                                    if (!event.relatedTarget || !root.contains(event.relatedTarget)) {
                                      validateField('geburtsdatum', formData.geburtsdatum);
                                    }
                                  },
                                  FormHelperTextProps: { 'data-testid': 'geburtsdatum-error' },
                                },
                              }}
                              maxDate={new Date()}
                            />
                          </LocalizationProvider>
                        </Grid>

                        {/* Staatsangehörigkeit */}
                        <Grid item xs={12} md={6} className="form-item-flex-2">
                          <FormControl fullWidth required error={!!errors.staatsangehoerigkeit}>
                            <InputLabel id="staatsangehoerigkeit-label" required>
                              Staatsangehörigkeit
                            </InputLabel>
                            <Select
                              inputRef={fieldRefs.staatsangehoerigkeit}
                              id="staatsangehoerigkeit"
                              labelId="staatsangehoerigkeit-label"
                              variant="outlined"
                              name="staatsangehoerigkeit"
                              value={formData.staatsangehoerigkeit}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              required
                              label="Staatsangehörigkeit"
                              className="select-height-fix"
                              sx={{
                                // Border immer mit --color-secondary
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'var(--color-secondary) !important',
                                },
                                // Focus-Zustand für das Select-Feld
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  border: '2px solid var(--color-secondary) !important',
                                },
                                // Schriftfarbe für den ausgewählten Wert
                                '& .MuiSelect-select': {
                                  color: 'var(--color-primary-text) !important',
                                },
                              }}
                            >
                              {countryCodes
                                .filter(country => country.order)
                                .sort((a, b) => a.order - b.order)
                                .map(country => (
                                  <MenuItem key={country.code} value={country.code}>
                                    {country.label}
                                  </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText data-testid="staatsangehoerigkeit-error">
                              {errors.staatsangehoerigkeit}
                            </FormHelperText>
                          </FormControl>
                        </Grid>
                      </Box>

                      {/* Adresse Überschrift */}
                      <Grid item xs={12}>
                        <Typography variant="h2" className="headline-medium-bold" sx={{ mt: 2, mb: 1 }}>
                          Deine aktuelle Adresse
                        </Typography>
                      </Grid>

                      <Box className="form-input-flex">
                        {/* PLZ */}
                        <Grid item xs={12} md={3}>
                          <TextField
                            inputRef={fieldRefs.plz}
                            variant="filled"
                            className="input-field larger-input-text"
                            fullWidth
                            label="PLZ"
                            name="plz"
                            value={formData.plz}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            error={!!errors.plz}
                            helperText={errors.plz}
                            autoComplete="postal-code"
                            inputProps={{
                              maxLength: 5,
                              inputMode: 'numeric',
                              pattern: '[0-9]*',
                            }}
                            onKeyPress={event => {
                              if (!/[0-9]/.test(event.key)) {
                                event.preventDefault();
                              }
                            }}
                            slotProps={{
                              formHelperText: { 'data-testid': 'plz-error' },
                            }}
                          />
                        </Grid>

                        {/* Ort */}
                        <Grid item xs={12} md={9} className="form-item-flex-6">
                          <TextField
                            inputRef={fieldRefs.ort}
                            variant="filled"
                            className="input-field larger-input-text"
                            fullWidth
                            label="Ort"
                            name="ort"
                            value={formData.ort}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            error={!!errors.ort}
                            helperText={errors.ort}
                            autoComplete="address-level2"
                            slotProps={{
                              formHelperText: { 'data-testid': 'ort-error' },
                            }}
                          />
                        </Grid>
                      </Box>

                      <Box className="form-input-flex">
                        {/* Straße */}
                        <Grid item xs={12} md={9} className="form-item-flex-6">
                          <TextField
                            inputRef={fieldRefs.strasse}
                            variant="filled"
                            className="input-field larger-input-text"
                            fullWidth
                            label="Straße"
                            name="strasse"
                            value={formData.strasse}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            error={!!errors.strasse}
                            helperText={errors.strasse}
                            autoComplete="street-address"
                            slotProps={{
                              formHelperText: { 'data-testid': 'strasse-error' },
                            }}
                          />
                        </Grid>

                        {/* Hausnummer */}
                        <Grid item xs={12} md={3}>
                          <TextField
                            inputRef={fieldRefs.hausnummer}
                            variant="filled"
                            className="input-field larger-input-text"
                            fullWidth
                            label="Nr."
                            name="hausnummer"
                            value={formData.hausnummer}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            required
                            error={!!errors.hausnummer}
                            helperText={errors.hausnummer}
                            autoComplete="address-line2"
                            slotProps={{
                              formHelperText: { 'data-testid': 'hausnummer-error' },
                            }}
                          />
                        </Grid>
                      </Box>

                      {/* Kontakt Überschrift */}
                      <Grid item xs={12}>
                        <Typography variant="h2" className="headline-medium-bold" sx={{ mt: 2, mb: 1 }}>
                          Deine Kontaktdaten
                        </Typography>
                      </Grid>

                      {/* E-Mail */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          inputRef={fieldRefs.email}
                          variant="filled"
                          className="input-field larger-input-text"
                          fullWidth
                          label="E-Mail"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          required
                          error={!!errors.email}
                          helperText={errors.email}
                          disabled={state.editMode}
                          slotProps={{
                            formHelperText: { 'data-testid': 'email-error' },
                          }}
                        />
                      </Grid>

                      {/* Telefonnummer */}
                      <Grid item xs={12} md={6}>
                        <TextField
                          inputRef={fieldRefs.telefonnummer}
                          variant="filled"
                          className="input-field larger-input-text"
                          fullWidth
                          label="Telefonnummer (optional)"
                          name="telefonnummer"
                          value={formData.telefonnummer}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          error={!!errors.telefonnummer}
                          helperText={errors.telefonnummer}
                          autoComplete="tel"
                          slotProps={{
                            formHelperText: { 'data-testid': 'telefonnummer-error' },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Sticky InfoboxSchutz für Desktop */}
                  {isDesktop && (
                    <div className="infobox-sidebar-container">
                      <InfoboxSchutz />
                    </div>
                  )}
                </Grid>

                {/* Navigation Buttons */}
                <Box className="button-container" sx={{ mt: 4 }}>
                  <Button
                    className="button-general button-normal"
                    variant="contained"
                    onClick={handleBack}
                    aria-label="Zurück"
                  >
                    Zurück
                  </Button>
                  <Button
                    className="button-general button-action"
                    variant="contained"
                    type="submit"
                    aria-label={(state.editMode && 'Weiter') || 'E-Mail bestätigen'}
                  >
                    {(state.editMode && 'Weiter') || 'E-Mail bestätigen'}
                  </Button>
                </Box>
              </Box>
            </Box>{' '}
            {/* className="page-content" */}
            {!state.editMode && (
              <Box>
                <Box variant="body2" component="p" sx={{ mt: 4, mb: 2 }}>
                  Bei Klick auf „E-Mail bestätigen“ wird eine E-Mail an deine angegebene E-Mail-Adresse versandt. Diese
                  E-Mail beinhaltet neben der Erstinformation die vorvertraglichen Unterlagen (u.a.
                  Versicherungsbedingungen und Kundeninformation, Produktinformationsblatt, Vertragsinformationen inkl.
                  Widerrufsbelehrung, Datenschutzerklärung).
                </Box>
                <Box variant="body2" component="p" sx={{ mt: 4, mb: 2 }}>
                  In der E-Mail befindet sich ein Bestätigungsbutton über den du zurück zur Beantragung deiner R+V
                  Hardwareversicherung gelangst.
                </Box>
                <Box variant="body2" component="p" sx={{ mt: 4, mb: 2 }}>
                  Bitte prüfe auch deinen SPAM-Ordner, falls die Unterlagen in den nächsten Minuten nicht bei dir
                  eintreffen.
                </Box>
              </Box>
            )}
            {/* --- ENDE PAGE CONTENT  --- */}
          </Box>{' '}
          {/* className="main-content-with-sidebar" */}
        </Container>{' '}
        {/* className="main-content-scroll-container" */}
      </Box>{' '}
      {/* className="wrapper" */}
    </LocalizationProvider>
  );
};

export default PersoenlicheAngaben;
